/**
 * CTA Analytics — Lightweight click tracking for conversion analysis
 *
 * Tracks which CTAs users click on and whether those clicks lead to enquiries.
 * Uses sendBeacon for non-blocking fire-and-forget delivery.
 * No cookies, no PII — just an anonymous session hash in localStorage (4-hour expiry).
 * localStorage keeps the hash consistent across tabs; expiry ensures return visits
 * after 4 hours generate a fresh session, giving accurate unique visitor counts.
 */

const ACT_URL = '/?ACT=' + (document.body.getAttribute('data-act-analytics') || '');
const SESSION_TTL = 4 * 60 * 60 * 1000; // 4 hours in ms
let sessionHash = null;

/**
 * Get or create anonymous session hash (localStorage, 4-hour expiry)
 */
function getSessionHash() {
    if (sessionHash) return sessionHash;

    try {
        const stored = localStorage.getItem('oe_analytics_session');
        if (stored) {
            const { hash, expires } = JSON.parse(stored);
            if (hash && expires && Date.now() < expires) {
                sessionHash = hash;
                return sessionHash;
            }
        }
        // Generate fresh 32-char hex hash
        const arr = new Uint8Array(16);
        crypto.getRandomValues(arr);
        sessionHash = Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
        localStorage.setItem('oe_analytics_session', JSON.stringify({
            hash: sessionHash,
            expires: Date.now() + SESSION_TTL
        }));
    } catch (e) {
        // localStorage unavailable — generate ephemeral hash
        const arr = new Uint8Array(16);
        crypto.getRandomValues(arr);
        sessionHash = Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
    }

    return sessionHash;
}

/**
 * Detect current page type from body classes or URL
 */
function getPageType() {
    var path = location.pathname;
    if (path.indexOf('/details/') !== -1) return 'detail';
    if (path.indexOf('/serviced-offices') !== -1) return 'listing';
    if (path === '/' || path === '') return 'home';
    if (path.indexOf('/guide') !== -1) return 'guide';
    return 'other';
}

/**
 * Get entry ID from the page (detail pages only)
 */
function getEntryId() {
    // Try data attribute on body or admin panel
    var el = document.querySelector('[data-entry-id]');
    if (el) return parseInt(el.dataset.entryId, 10) || 0;

    // Try the admin panel entry ID
    var adminId = document.querySelector('.admin-panel__id-value');
    if (adminId) return parseInt(adminId.textContent, 10) || 0;

    return 0;
}

/**
 * Send a beacon with CSRF token via FormData (EE expects CSRF as a form field)
 */
function sendPayload(data) {
    var fd = new FormData();
    fd.append('csrf_token', window.EE_CSRF_TOKEN || '');
    for (var key in data) {
        if (data.hasOwnProperty(key)) {
            fd.append(key, data[key]);
        }
    }

    if (navigator.sendBeacon) {
        navigator.sendBeacon(ACT_URL, fd);
    } else {
        fetch(ACT_URL, {
            method: 'POST',
            body: fd,
            keepalive: true
        }).catch(function() {});
    }
}

/**
 * Send a tracking event via sendBeacon (non-blocking)
 */
function track(ctaType, entryId) {
    if (!ACT_URL || ACT_URL === '/?ACT=') return;

    var payload = {
        action: 'cta_click',
        cta_type: ctaType,
        page_type: getPageType(),
        entry_id: entryId || getEntryId(),
        session_hash: getSessionHash(),
        screen_width: screen.width,
        screen_height: screen.height
    };

    var entryCountry = (document.body.getAttribute('data-entry-country') || '').trim();
    if (entryCountry) {
        payload.country_code = entryCountry;
    }

    sendPayload(payload);
}

/**
 * Record form progress when a form is closed or submitted
 */
function trackFormProgress(maxStage, timeInForm, abandoned, formFields) {
    if (!ACT_URL || ACT_URL === '/?ACT=') return;

    var payload = {
        action: 'form_progress',
        session_hash: getSessionHash(),
        max_stage: maxStage,
        time_in_form: timeInForm,
        abandoned: abandoned ? 1 : 0
    };

    if (formFields) {
        if (formFields.people)     payload.workspace_people = formFields.people;
        if (formFields.officeType) payload.office_type      = formFields.officeType;
        if (formFields.duration)   payload.duration         = formFields.duration;
    }

    sendPayload(payload);
}

/**
 * Auto-attach click tracking to CTA elements
 */
function initCtaTracking() {
    // Map CSS selectors to CTA types
    var ctaMap = [
        { selector: '[data-form-type="quote"]', type: 'quote' },
        { selector: '[data-form-type="viewing"]', type: 'viewing' },
        { selector: '[data-form-type="callback"]', type: 'callback' },
        { selector: '[data-form-type="question"]', type: 'question' },
        { selector: '[data-form-type="shortlist"], [data-shortlist-add]', type: 'shortlist' },
        { selector: '[href*="/pdf/"], [href*="pdf/office"]', type: 'pdf' },
        { selector: '.sticky-cta', type: 'sticky_cta' },
        { selector: '.chat-trigger, .start-chat', type: 'chatbot_open' },
        { selector: '.search-btn', type: 'search' },
        { selector: '[href^="tel:"]', type: 'phone' }
    ];

    // Use capture phase so we fire BEFORE any stopPropagation() in other handlers
    document.addEventListener('click', function(e) {
        // Ignore clicks inside an open dialog/form overlay — only track the initial CTA click
        if (e.target.closest('dialog[open], .form-dialog, .qq-form, .chatbot-overlay, .form-submitting')) {
            return;
        }

        for (var i = 0; i < ctaMap.length; i++) {
            var cta = e.target.closest(ctaMap[i].selector);
            if (cta) {
                // Skip shortlist removals — only track additions
                if (ctaMap[i].type === 'shortlist' && cta.classList.contains('in-shortlist')) {
                    break;
                }
                var entryId = parseInt(cta.dataset.entryId, 10) || getEntryId();
                var ctaType = cta.dataset.category || ctaMap[i].type;
                track(ctaType, entryId);
                break;
            }
        }
    }, true);
}

// Ensure session hash exists from page load so it's always available for form submission
getSessionHash();

// Initialise on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCtaTracking);
} else {
    initCtaTracking();
}

// Export for use by form submission code
export { track, trackFormProgress, getSessionHash };
